import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTrigger } from 'shared/components/ui/dialog';
import { Input } from 'shared/components/ui/input';
import { FinecoButton } from '../../../../FinecoButton';
import colorsStore from 'shared/store/colorsStore';

import styles from './RenameListModal.module.scss';

type RenameListModalProps = {
  currentName: string;
  onRename: (newName: string) => Promise<void>;
  children: React.ReactNode;
};

export const RenameListModal: React.FC<RenameListModalProps> = ({ currentName, onRename, children }) => {
  const { t } = useTranslation();
  // Subscribes to color-config changes so the component re-renders when they update.
  colorsStore((state) => state.colorsConfigStore);

  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState(currentName);
  const [submitting, setSubmitting] = useState(false);

  // Reset name when modal opens
  useEffect(() => {
    if (open) {
      setNewName(currentName);
    }
  }, [open, currentName]);

  const handleSubmit = async () => {
    if (!newName.trim() || newName === currentName) {
      return;
    }

    setSubmitting(true);

    try {
      await onRename(newName.trim());
      setOpen(false);
    } catch (error) {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNewName(currentName);
    setOpen(false);
  };

  const isSubmitDisabled = submitting || !newName.trim() || newName === currentName;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent title={t('instrumentLists.renameList')} className={styles.dialogContent} centerTitle>
        <div className={styles.modalBody}>
          <div className={styles.nameInputContainer}>
            <Input
              id="newListName"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t('instrumentLists.enterListName')}
              disabled={submitting}
              autoFocus
            />
          </div>
        </div>

        <div className={styles.modalFooter}>
          <FinecoButton
            className={styles.cancelButton}
            click={handleCancel}
            variant="white"
            copy={t('actions.cancel')}
            disabled={submitting}
          />
          <FinecoButton
            className={styles.renameButton}
            click={handleSubmit}
            copy={submitting ? t('actions.submitting') : t('instrumentLists.renameList')}
            disabled={isSubmitDisabled}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
